/**
 * Mock implementations for storyCanvasApi — realistic fixtures that mirror
 * the exact shapes the backend returns so the canvas renders correctly in
 * local dev without a running server.
 *
 * Staggered delays simulate widgets arriving one by one, the same way the
 * live flow works (manifest fast, each widget slightly delayed).
 *
 * Routing:
 *   storyId === "mock-story-uuid-001" → Drug X memo fixtures (storyCanvasMemoApi.mock.ts)
 *   anything else                     → default canvas fixtures below
 */
import type { ChartItemPositionApi } from "../chartStudio/chartStudioV2.types";
import type { VisualizationSpec } from "vega-embed";
import type {
  AxisFormatApi,
  BindingApi,
  CanvasDatasetApi,
  CanvasItemAddRequest,
  CanvasItemFullApi,
  CanvasItemListResponseApi,
  CanvasItemListTypeFilterApi,
  CanvasItemPatchRequestApi,
  CanvasItemRowTypeApi,
  CanvasItemStubApi,
  CanvasItemSummaryApi,
  CanvasItemType,
  CanvasItemTypeApi,
  FormatApi,
  PaletteRefApi,
  QueryModelApi,
  CanvasItemStatusDecision,
  CanvasItemVisibilityUpdateRequest,
  CanvasManifestApi,
  CanvasSearchResponseApi,
  CanvasSearchResultApi,
  CanvasSearchResultType,
  CanvasWidgetApi,
  ItemPositionApi,
  CanvasContentItemApi,
  CanvasContentItemCreateRequestApi,
  CanvasContentItemUpdateRequestApi,
  CanvasItemSourceUpdateRequestApi,
  VisibilityMap,
} from "./storyCanvas.types";
import {
  mockFetchMemoCanvasManifest,
  mockFetchMemoCanvasWidget,
  mockFetchMemoCanvasDataset,
} from "./storyCanvasMemoApi.mock";
import { mergePatch } from "./canvasItemMappers";
import { applyStoryFiltersToItem } from "./storyFilterItemState.mock";
import { assertMockStoryEditor, listMockStoryFilterRows } from "./storyFilters.mock";
import { MOCK_MODEL_RELEASE_ID } from "../chartStudio/chartStudioDataModel.mock";
import { findMockColorPalette } from "../chartStudio/colorPalette.mock";
import { applyChartFormatToSpec } from "../chartStudio/applyChartFormatToSpec";
import { applyPaletteToSpec } from "../chartStudio/applyPaletteToSpec";
import { readChartFormat } from "../chartStudio/readChartFormat";
import { chartColorPaletteDefinitions, chartFormatDefaults } from "../../components/registry";
import {
  mockConflictError,
  mockNotFoundError,
  mockValidationError,
} from "../mockDomainError";

const MOCK_STORY_ID = "mock-story-uuid-001";
const MOCK_WORKSPACE_ID = "mock-workspace-uuid-001";

/** Simulated network latency in ms (manifest is fast, widgets trickle in). */
const MANIFEST_LATENCY_MS = 200;
const WIDGET_LATENCY_MS: Record<string, number> = {
  "widget-kpi-1": 400,
  "widget-kpi-2": 600,
  "widget-kpi-3": 800,
  "widget-narrative-1": 1000,
  "widget-chart-1": 1400,
  "widget-chart-2": 1800,
  "widget-insight-1": 2200,
  "widget-captured-report-1": 2600,
};
const DATASET_LATENCY_MS = 300;
const SEARCH_LATENCY_MS = 250;

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ─── Manifest fixture ──────────────────────────────────────────────────────────

const MOCK_MANIFEST: CanvasManifestApi = {
  canvas_id: "mock-canvas-uuid-001",
  status: "ready",
  items: [
    {
      item_id: "widget-kpi-1",
      type: "kpi",
      position: { x: 0, y: 0, w: 4, h: 4 },
      parent_item_id: null,
      visibility: null,
      status: "accepted",
    },
    {
      item_id: "widget-kpi-2",
      type: "kpi",
      position: { x: 4, y: 0, w: 4, h: 4 },
      parent_item_id: null,
      visibility: null,
      status: "accepted",
    },
    {
      item_id: "widget-kpi-3",
      type: "kpi",
      position: { x: 8, y: 0, w: 4, h: 4 },
      parent_item_id: null,
      visibility: null,
      status: "accepted",
    },
    {
      item_id: "widget-narrative-1",
      type: "insight",
      position: { x: 0, y: 4, w: 12, h: 6 },
      parent_item_id: null,
      visibility: null,
      status: null,
    },
    {
      item_id: "widget-chart-1",
      type: "chart",
      position: { x: 0, y: 10, w: 12, h: 20 },
      parent_item_id: null,
      visibility: null,
      status: "accepted",
    },
    {
      item_id: "widget-chart-2",
      type: "chart",
      position: { x: 0, y: 30, w: 12, h: 21 },
      parent_item_id: null,
      visibility: null,
      status: "accepted",
    },
    {
      item_id: "widget-insight-1",
      type: "insight",
      position: { x: 0, y: 51, w: 12, h: 21 },
      parent_item_id: null,
      visibility: null,
      status: null,
    },
    {
      // Simulates a report dropped via the browser-extension "+ Add to
      // MagicBI" plugin: position arrives null (not yet placed) — the canvas
      // auto-places it (apiPositionLayout.ts) until the user drags it, which
      // fires the existing update-position PATCH exactly like every other
      // widget type.
      item_id: "widget-captured-report-1",
      type: "captured_widget",
      position: null,
      parent_item_id: null,
      visibility: null,
      status: "accepted",
    },
  ],
};

// ─── Widget fixtures ───────────────────────────────────────────────────────────

const MOCK_WIDGETS: Record<string, CanvasWidgetApi> = {
  "widget-kpi-1": {
    item_id: "widget-kpi-1",
    type: "kpi",
    name: "Total Revenue",
    value: "$4.2M",
    delta: "+$380K",
    delta_pct: "+9.9%",
    trend: "up",
    comparison_label: "vs last quarter",
    footer_source: { name: "Qlik", logo_src: "/logos/qlik.svg" },
  },
  "widget-kpi-2": {
    item_id: "widget-kpi-2",
    type: "kpi",
    name: "New Prescribers",
    value: "1,284",
    delta: "+142",
    delta_pct: "+12.4%",
    trend: "up",
    comparison_label: "vs last quarter",
    footer_source: { name: "Tableau", logo_src: "/logos/tableau.svg" },
  },
  "widget-kpi-3": {
    item_id: "widget-kpi-3",
    type: "kpi",
    name: "Market Share",
    value: "18.3%",
    delta: "-0.4%",
    delta_pct: "-2.1%",
    trend: "down",
    comparison_label: "vs last quarter",
    footer_source: { name: "Qlik", logo_src: "/logos/qlik.svg" },
  },
  "widget-narrative-1": {
    item_id: "widget-narrative-1",
    type: "insight",
    bullets: [],
    sections: [
      {
        id: "widget-narrative-1-summary",
        kind: "summary",
        title: "AI summary",
        body: "DrugX achieved strong revenue growth of 9.9% QoQ driven by expansion into three new territories. New prescriber acquisition accelerated in the Northeast region, while market share saw slight contraction in the West due to competitor activity. The launch trajectory remains on track against the 12-month plan.",
        collapsible: false,
      },
    ],
  },
  /** Live story narrative card shape — Edit manually PATCHes body + format only. */
  "widget-story-narrative-1": {
    item_id: "widget-story-narrative-1",
    type: "narrative",
    body: "Customer-related concepts dominate what was discovered, followed by Product and Finance-related concepts.",
    format: "plain",
    parent_item_id: null,
  },
  /** Live story summary card shape — Edit manually PATCHes body + format only. */
  "widget-story-summary-1": {
    item_id: "widget-story-summary-1",
    type: "summary",
    title: "AI summary",
    body: "Executive summary of the story findings.",
    format: "plain",
    parent_item_id: null,
  },
  "widget-chart-1": {
    item_id: "widget-chart-1",
    type: "chart",
    title: "Revenue performance vs target",
    spec: {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      mark: { type: "bar", tooltip: true },
      encoding: {
        x: { field: "region", type: "nominal", title: "Region" },
        y: { field: "revenue", type: "quantitative", title: "Revenue ($)" },
        color: { field: "quarter", type: "nominal" },
      },
      data: { values: [] },
    },
    narrative: null,
    modes: [
      { id: "financial", label: "Financial" },
      { id: "market-share", label: "Market share" },
      { id: "regional", label: "Regional" },
      { id: "pattern", label: "Pattern" },
    ],
    active_mode_id: "market-share",
    footer_chips: [
      { id: "chip-revenue-growth", label: "Revenue growth" },
      { id: "chip-target-achieve", label: "Target achieve" },
    ],
    footer_more_count: 2,
    footer_source: {
      name: "Tableau",
      href: "https://www.tableau.com",
    },
    // Seeded so the summary panel (and its Edit/Delete menu) is testable in
    // the UI out of the box; chart-2 starts empty to cover the other state.
    ai_summary: {
      title: ">109% achievement",
      body: "Drug X has consistently **exceeded sales targets over the past 6 months**, with particularly strong performance in June-July 2024 (>109% achievement). Recent months show stabilized growth at **~105–106% of target.**",
    },    
    data_id: "dat-chart-1",
  },
  "widget-chart-2": {
    item_id: "widget-chart-2",
    type: "chart",
    title: "New Prescribers by Month",
    spec: {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      mark: { type: "line", tooltip: true },
      encoding: {
        x: { field: "month", type: "ordinal", title: "Month" },
        y: { field: "count", type: "quantitative", title: "New Prescribers" },
      },
      data: { values: [] },
    },

    modes: [
      { id: "financial", label: "Financial" },
      { id: "market-share", label: "Market share" },
      { id: "regional", label: "Regional" },
      { id: "pattern", label: "Pattern" },
    ],
    active_mode_id: "pattern",
    footer_chips: [
      { id: "chip-prescriber-growth", label: "Prescriber growth" },
      { id: "chip-northeast", label: "Northeast" },
    ],
    footer_more_count: 1,
    footer_source: {
      name: "Tableau",
      href: "https://www.tableau.com",
    },
    // Starts empty — filled when a narrative edit persists via the mock store.
    narrative: null,
    ai_summary: null,
    data_id: "dat-chart-2",
  },
  "widget-insight-1": {
    item_id: "widget-insight-1",
    type: "insight",
    bullets: [
      "Northeast accounts for 41% of total revenue despite covering only 28% of target prescribers.",
      "Prescriber conversion rate improved 3.2 pts QoQ following the updated detailing materials rollout.",
      "West region market share loss is concentrated in three metro markets with high generic competition.",
      "Time-to-first-script for new prescribers shortened from 18 to 12 days, indicating improved rep effectiveness.",
      "Top 20 accounts by volume represent 62% of revenue — retention risk is concentrated and manageable.",
    ],
  },
  "widget-captured-report-1": {
    item_id: "widget-captured-report-1",
    type: "captured_widget",
    source: { id: "ds-tableau", name: "Tableau", logo_url: "/logos/tableau.svg" },
    // Real, working connection — used as-is so useConnectorEmbedToken's
    // GET /api/v1/connectors/embed-token round-trips against the real backend
    // even though the canvas item itself is mocked.
    connector_asset_id: "asset-captured-report-1",
    connection_id: "39bcc888-fc25-4046-9202-a0463d54bb51",
    title: "Global Indicators",
    embed_url:
      "https://prod-in-a.online.tableau.com/t/fli0l5klqrc5-b49bd6dd6c/views/WorldIndicators/GlobalIndicators?:embed=yes",
    status: "accepted",
  },
};

// ─── Dataset fixtures ──────────────────────────────────────────────────────────

const MOCK_DATASETS: Record<string, CanvasDatasetApi> = {
  "dat-chart-1": {
    dataset_id: "dat-chart-1",
    rows: [
      { region: "Northeast", quarter: "Q1", revenue: 820000 },
      { region: "Northeast", quarter: "Q2", revenue: 1001000 },
      { region: "Southeast", quarter: "Q1", revenue: 540000 },
      { region: "Southeast", quarter: "Q2", revenue: 612000 },
      { region: "Midwest", quarter: "Q1", revenue: 390000 },
      { region: "Midwest", quarter: "Q2", revenue: 418000 },
      { region: "West", quarter: "Q1", revenue: 710000 },
      { region: "West", quarter: "Q2", revenue: 682000 },
    ],
    page: 1,
    page_size: 100,
    total: 8,
  },
  "dat-chart-2": {
    dataset_id: "dat-chart-2",
    rows: [
      { month: "Jan", count: 180 },
      { month: "Feb", count: 195 },
      { month: "Mar", count: 210 },
      { month: "Apr", count: 238 },
      { month: "May", count: 261 },
      { month: "Jun", count: 284 },
    ],
    page: 1,
    page_size: 100,
    total: 6,
  },
};

// ─── Canvas search fixtures ────────────────────────────────────────────────────

const MOCK_SEARCH_RESULTS: CanvasSearchResultApi[] = [
  {
    result_id: "search-report-1",
    source: "tableau",
    type: "report",
    title: "Revenue by Region — Q1 vs Q2",
    connector_asset_id: "asset-revenue-by-region",
    canvas_item_id: null,
    story_title: null,
    thumbnail_url: null,
  },
  {
    result_id: "search-report-2",
    source: "powerbi",
    type: "report",
    title: "Revenue by Channel — Q1 vs Q2",
    connector_asset_id: "asset-revenue-by-channel",
    canvas_item_id: null,
    story_title: null,
    thumbnail_url: null,
  },
  {
    result_id: "search-report-3",
    source: "looker",
    type: "report",
    title: "Revenue by Product Line",
    connector_asset_id: "asset-revenue-by-product-line",
    canvas_item_id: null,
    story_title: null,
    thumbnail_url: null,
  },
  {
    result_id: "search-chart-1",
    source: "native",
    type: "chart",
    title: "Monthly Revenue Trends",
    connector_asset_id: null,
    canvas_item_id: "widget-chart-1",
    story_title: "DrugX Q2 performance review",
    thumbnail_url: null,
  },
  {
    result_id: "search-chart-2",
    source: "native",
    type: "chart",
    title: "Sales by Region — Q1 vs Q2",
    connector_asset_id: null,
    canvas_item_id: "widget-chart-2",
    story_title: "DrugX Q2 performance review",
    thumbnail_url: null,
  },
  {
    result_id: "search-kpi-1",
    source: "native",
    type: "kpi",
    title: "Total Revenue",
    connector_asset_id: null,
    canvas_item_id: "widget-kpi-1",
    story_title: "DrugX Q2 performance review",
    thumbnail_url: null,
  },
  {
    result_id: "search-report-4",
    source: "metabase",
    type: "report",
    title: "Sales Pipeline Overview",
    connector_asset_id: "asset-sales-pipeline",
    canvas_item_id: null,
    story_title: null,
    thumbnail_url: null,
  },
];

// ─── Mutable widget overlay ────────────────────────────────────────────────────
// Narrative/summary edits persist here so the post-save React Query refetch
// returns the edited text — mirroring exactly what the live backend does.
// Base fixtures above stay pristine (tests rely on them).

const widgetOverrides: Record<string, CanvasWidgetApi> = {};

/** The canvas's singleton story-narrative widget the story-level API targets. */
export const MOCK_STORY_NARRATIVE_WIDGET_ID = "widget-narrative-1";

const currentWidget = (widgetId: string): CanvasWidgetApi | undefined =>
  widgetOverrides[widgetId] ?? MOCK_WIDGETS[widgetId];

/** Persist a story-level edit onto the singleton story card.
 *  Supports `narrative` / `summary` body widgets and legacy `insight` sections. */
export function applyMockStoryNarrative(update: {
  summary?: string | null;
  narrative?: string | null;
}): void {
  const widget = currentWidget(MOCK_STORY_NARRATIVE_WIDGET_ID);
  if (!widget) return;

  if (widget.type === "narrative") {
    widgetOverrides[MOCK_STORY_NARRATIVE_WIDGET_ID] = {
      ...widget,
      ...(update.narrative != null ? { body: update.narrative } : {}),
      // Story-summary family can target the same singleton — land on body.
      ...(update.summary != null && update.narrative == null
        ? { body: update.summary }
        : {}),
      ...(update.summary != null && update.narrative != null
        ? { title: update.summary }
        : {}),
    };
    return;
  }

  if (widget.type === "summary") {
    widgetOverrides[MOCK_STORY_NARRATIVE_WIDGET_ID] = {
      ...widget,
      body: update.summary ?? update.narrative ?? widget.body,
    };
    return;
  }

  if (widget.type !== "insight") return;
  let sections = (widget.sections ?? []).map((section) => {
    if (section.kind === "summary" && update.summary != null)
      return { ...section, body: update.summary };
    if (section.kind === "narrative" && update.narrative != null)
      return { ...section, body: update.narrative };
    return section;
  });
  if (
    update.narrative != null &&
    !sections.some((section) => section.kind === "narrative")
  ) {
    sections = [
      ...sections,
      {
        id: `${MOCK_STORY_NARRATIVE_WIDGET_ID}-narrative`,
        kind: "narrative",
        title: "AI narrative",
        body: update.narrative,
        collapsible: true,
      },
    ];
  }
  widgetOverrides[MOCK_STORY_NARRATIVE_WIDGET_ID] = { ...widget, sections };
}

/** Persist a chart/kpi item edit: the saved text becomes both the chart's
 *  narrative and its AI-summary card body (what the canvas renders). */
export function applyMockCanvasItemNarrative(
  itemId: string,
  narrative: string,
): void {
  const widget = currentWidget(itemId);
  if (!widget || widget.type !== "chart") return;
  widgetOverrides[itemId] = {
    ...widget,
    narrative,
    ai_summary: {
      ...(widget.ai_summary ?? { title: "AI summary" }),
      body: narrative,
    },
  };
}

/** Persist a chart/kpi SUMMARY edit (override/improve/generate): only the
 *  AI-summary card body changes — the chart's narrative is a separate field
 *  owned by the narrative endpoints. */
export function applyMockCanvasItemSummary(itemId: string, text: string): void {
  const widget = currentWidget(itemId);
  if (!widget || widget.type !== "chart") return;
  widgetOverrides[itemId] = {
    ...widget,
    ai_summary: {
      ...(widget.ai_summary ?? { title: "AI summary" }),
      body: text,
    },
  };
}

/** Persist a summary-only delete: the chart stays, its narrative + AI summary
 *  card are cleared — what the DELETE item-narrative endpoint does. */
export function clearMockCanvasItemNarrative(itemId: string): void {
  const widget = currentWidget(itemId);
  if (!widget || widget.type !== "chart") return;
  widgetOverrides[itemId] = { ...widget, narrative: null, ai_summary: null };
}

// Position moves/resizes persisted by the PATCH position mock — the manifest
// fetch merges these over the base stubs, like the live backend would.
const positionOverrides: Record<string, ItemPositionApi> = {};

/** Content-visibility flags persisted by PATCH content-visibility. */
const visibilityOverrides: Record<string, VisibilityMap> = {};

// Items soft-deleted by the delete mock — the manifest fetch filters them out
// so the widget actually drops off the canvas on refetch.
const deletedItemIds = new Set<string>();

// Accept/reject decisions persisted by the status mock so a manifest refetch
// reflects the new item status (accepted) or drops it (rejected → deleted).
const statusOverrides: Record<string, string> = {};

// Search-added items are appended to the manifest and widget stores so mock
// mode follows the same POST → manifest → widget loading path as the live API.
const addedItemStubs: CanvasItemStubApi[] = [];
let addedItemCounter = 0;

const findMockStub = (itemId: string): CanvasItemStubApi | undefined =>
  [...MOCK_MANIFEST.items, ...addedItemStubs].find(
    (item) => item.item_id === itemId,
  );

/** Merge a partial position change into an item's stored position and persist
 *  it. Returns the updated stub (what the live PATCH endpoint responds with). */
export function applyMockCanvasItemPosition(
  itemId: string,
  position: Partial<ItemPositionApi>,
): CanvasItemStubApi {
  const stub = findMockStub(itemId);
  if (!stub)
    throw new Error(`applyMockCanvasItemPosition: unknown item "${itemId}"`);
  const merged = { ...(positionOverrides[itemId] ?? stub.position), ...position };
  positionOverrides[itemId] = merged;
  return resolveMockStub(stub);
}

/** Apply an accept/reject decision. `accepted` persists the new status so the
 *  manifest refetch shows the item confirmed; `rejected` soft-deletes it so the
 *  refetch drops it off the canvas (mirroring the live backend). */
export function applyMockCanvasItemStatus(
  itemId: string,
  status: CanvasItemStatusDecision,
): CanvasItemStubApi {
  const stub = findMockStub(itemId);
  if (!stub)
    throw new Error(`applyMockCanvasItemStatus: unknown item "${itemId}"`);
  if (status === "rejected") {
    deletedItemIds.add(itemId);
  } else {
    statusOverrides[itemId] = status;
  }
  return { ...resolveMockStub(stub), status };
}

/** Merge content-visibility flags — live GET returns a FLAT visibility object.
 *
 * Content upserts must match the target widget GET shape:
 * - Chart → `ai_summary` / `narrative`
 * - Story narrative/summary card → `body` + `format` (never invent chart fields)
 */
export function applyMockCanvasItemVisibility(
  itemId: string,
  body: CanvasItemVisibilityUpdateRequest,
): CanvasItemStubApi {
  const stub = findMockStub(itemId);
  if (!stub)
    throw new Error(`applyMockCanvasItemVisibility: unknown item "${itemId}"`);
  const existing = visibilityOverrides[itemId] ?? stub.visibility ?? {};
  const next: VisibilityMap = { ...existing };
  if (body.visibility?.is_summary_visible !== undefined) {
    next.is_summary_visible = body.visibility.is_summary_visible;
  }
  if (body.visibility?.is_narrative_visible !== undefined) {
    next.is_narrative_visible = body.visibility.is_narrative_visible;
  }
  visibilityOverrides[itemId] = next;

  // Story-level narrative/summary card — body + format only.
  if (body.body !== undefined) {
    const widget = currentWidget(itemId);
    if (widget?.type === "narrative" || widget?.type === "summary") {
      widgetOverrides[itemId] = {
        ...widget,
        body: body.body,
        ...(body.format !== undefined ? { format: body.format } : {}),
      };
    }
  }

  if (body.ai_summary) {
    const widget = currentWidget(itemId);
    if (widget?.type === "chart") {
      widgetOverrides[itemId] = {
        ...widget,
        ai_summary: {
          ...(widget.ai_summary ?? {}),
          title: body.ai_summary.title,
          body: body.ai_summary.body,
          ...(body.ai_summary.sections
            ? { sections: body.ai_summary.sections }
            : {}),
        },
      };
    }
  }
  if (body.narrative !== undefined) {
    const widget = currentWidget(itemId);
    if (widget?.type === "chart") {
      const existingSummary = widget.ai_summary;
      const sections = existingSummary?.sections?.length
        ? existingSummary.sections.map((s) =>
            s.kind === "narrative" || s.id.endsWith("-narrative")
              ? { ...s, body: body.narrative! }
              : s,
          )
        : existingSummary
          ? [
              {
                id: `${itemId}-summary`,
                kind: "summary" as const,
                title: "AI summary",
                body: existingSummary.body,
              },
              {
                id: `${itemId}-narrative`,
                kind: "narrative" as const,
                title: "AI narrative",
                body: body.narrative!,
                collapsible: true,
              },
            ]
          : [
              {
                id: `${itemId}-narrative`,
                kind: "narrative" as const,
                title: "AI narrative",
                body: body.narrative!,
                collapsible: true,
              },
            ];
      widgetOverrides[itemId] = {
        ...widget,
        narrative: body.narrative,
        ai_summary: existingSummary
          ? {
              title: existingSummary.title,
              body: existingSummary.body,
              sections,
            }
          : {
              title: "AI summary",
              body: "",
              sections,
            },
      };
    }
  }

  return resolveMockStub(stub);
}

function resolveMockStub(stub: CanvasItemStubApi): CanvasItemStubApi {
  return {
    ...stub,
    position: positionOverrides[stub.item_id] ?? stub.position,
    visibility: visibilityOverrides[stub.item_id] ?? stub.visibility ?? null,
    status: statusOverrides[stub.item_id] ?? stub.status,
  };
}

/** Clears all persisted mock mutations (edits, moves, deletes) — call between
 *  tests to keep fixtures deterministic. */
export function resetMockCanvasWidgetOverrides(): void {
  for (const key of Object.keys(widgetOverrides)) delete widgetOverrides[key];
  for (const key of Object.keys(positionOverrides))
    delete positionOverrides[key];
  for (const key of Object.keys(visibilityOverrides))
    delete visibilityOverrides[key];
  for (const key of Object.keys(statusOverrides)) delete statusOverrides[key];
  addedItemStubs.splice(0, addedItemStubs.length);
  addedItemCounter = 0;
  deletedItemIds.clear();
  for (const key of Object.keys(editableItems)) delete editableItems[key];
}

/** The default canvas's items as a manifest refetch would return them. */
const currentMockStubs = (): CanvasItemStubApi[] =>
  [...MOCK_MANIFEST.items, ...addedItemStubs]
    .filter((item) => !deletedItemIds.has(item.item_id))
    .map((item) => resolveMockStub(item));

// ─── Mock fetch functions ──────────────────────────────────────────────────────

export async function mockFetchCanvasManifest(
  workspaceId: string,
  storyId: string,
): Promise<CanvasManifestApi> {
  if (storyId === MOCK_STORY_ID) {
    return mockFetchMemoCanvasManifest(workspaceId, storyId);
  }
  await wait(MANIFEST_LATENCY_MS);
  return { ...MOCK_MANIFEST, items: currentMockStubs() };
}

export async function mockFetchCanvasWidget(
  workspaceId: string,
  storyId: string,
  widgetId: string,
): Promise<CanvasWidgetApi> {
  if (storyId === MOCK_STORY_ID) {
    return mockFetchMemoCanvasWidget(workspaceId, storyId, widgetId);
  }
  const delay = WIDGET_LATENCY_MS[widgetId] ?? 500;
  await wait(delay);
  const widget = currentWidget(widgetId);
  if (!widget)
    throw new Error(`mockFetchCanvasWidget: unknown widget "${widgetId}"`);
  const visibility =
    visibilityOverrides[widgetId] ??
    ("visibility" in widget ? widget.visibility : null) ??
    null;
  if (
    widget.type === "chart" ||
    widget.type === "kpi" ||
    widget.type === "summary" ||
    widget.type === "narrative"
  ) {
    return { ...widget, visibility: visibility ?? null };
  }
  return widget;
}

export async function mockFetchCanvasDataset(
  workspaceId: string,
  storyId: string,
  dataId: string,
): Promise<CanvasDatasetApi> {
  if (storyId === MOCK_STORY_ID) {
    return mockFetchMemoCanvasDataset(workspaceId, storyId, dataId);
  }
  await wait(DATASET_LATENCY_MS);
  const dataset = MOCK_DATASETS[dataId];
  if (!dataset)
    throw new Error(`mockFetchCanvasDataset: unknown data_id "${dataId}"`);
  return dataset;
}

/** Mock GET canvas-search — filters the fixture list by title substring +
 *  type, mirroring the backend's ILIKE + type-filter behaviour. */
export async function mockFetchCanvasSearch(
  _workspaceId: string,
  params: { q: string; types: CanvasSearchResultType[]; limit: number },
): Promise<CanvasSearchResponseApi> {
  await wait(SEARCH_LATENCY_MS);

  const q = params.q.trim().toLowerCase();
  const typeSet = new Set(params.types);

  const filtered = MOCK_SEARCH_RESULTS.filter((result) => {
    const matchesQuery = q === "" || result.title.toLowerCase().includes(q);
    const matchesType = typeSet.size === 0 || typeSet.has(result.type);
    return matchesQuery && matchesType;
  });

  const results = filtered.slice(0, params.limit);
  return { results, total: filtered.length };
}

/** Mock POST canvas-items — stores the requested frontend geometry verbatim. */
export async function mockAddCanvasItem(
  _workspaceId: string,
  _storyId: string,
  body: CanvasItemAddRequest,
): Promise<CanvasItemStubApi> {
  await wait(DATASET_LATENCY_MS);
  assertMockStoryEditor(ITEM_WRITE_FORBIDDEN);
  const searchResult = MOCK_SEARCH_RESULTS.find(
    (result) =>
      result.result_id === body.source_id ||
      result.connector_asset_id === body.source_id ||
      result.canvas_item_id === body.source_id,
  );
  const sourceWidget = currentWidget(body.source_id);
  const itemId = `mock-added-item-${++addedItemCounter}`;
  const itemType =
    body.source === "native" &&
    sourceWidget &&
    (sourceWidget.type === "chart" || sourceWidget.type === "kpi")
      ? sourceWidget.type
      : "ingested_widget";
  const stub: CanvasItemStubApi = {
    item_id: itemId,
    type: itemType,
    position: {
      x: body.pos_x,
      y: body.pos_y,
      w: body.pos_w,
      h: body.pos_h,
    },
    parent_item_id: null,
    visibility: null,
    status: "accepted",
  };

  if (sourceWidget && itemType !== "ingested_widget") {
    widgetOverrides[itemId] = { ...sourceWidget, item_id: itemId };
  } else {
    const sourceName =
      body.source === "powerbi"
        ? "Power BI"
        : `${body.source.charAt(0).toUpperCase()}${body.source.slice(1)}`;
    widgetOverrides[itemId] = {
      item_id: itemId,
      type: "ingested_widget",
      source: {
        id: body.source,
        name: sourceName,
        logo_url: `/logos/${body.source}.svg`,
      },
      connector_asset_id:
        searchResult?.connector_asset_id ?? body.source_id,
      connection_id: searchResult?.connection_id ?? null,
      title: searchResult?.title ?? "Added report",
      embed_url: searchResult?.embed_url ?? null,
      thumbnail_url: searchResult?.thumbnail_url ?? null,
      status: "accepted",
    };
  }

  addedItemStubs.push(stub);
  return stub;
}

/** Mock bulk PATCH update-position — persists geometry for each item. */
export async function mockUpdateCanvasItemsPositions(
  _workspaceId: string,
  _storyId: string,
  items: Array<{
    item_id: string;
    pos_x: number;
    pos_y: number;
    pos_w?: number;
    pos_h?: number;
    pos_h_fixed?: boolean;
  }>,
): Promise<CanvasItemStubApi[]> {
  await wait(DATASET_LATENCY_MS);
  assertMockStoryEditor(ITEM_WRITE_FORBIDDEN);
  return items.map((item) =>
    applyMockCanvasItemPosition(item.item_id, {
      x: item.pos_x,
      y: item.pos_y,
      ...(item.pos_w != null ? { w: item.pos_w } : {}),
      ...(item.pos_h != null ? { h: item.pos_h } : {}),
      ...(item.pos_h_fixed != null ? { h_fixed: item.pos_h_fixed } : {}),
    }),
  );
}

/** Mock PATCH content-visibility — persists story/presentation flags. */
export async function mockUpdateCanvasItemVisibility(
  _workspaceId: string,
  _storyId: string,
  itemId: string,
  body: CanvasItemVisibilityUpdateRequest,
): Promise<CanvasItemStubApi> {
  await wait(DATASET_LATENCY_MS);
  assertMockStoryEditor(ITEM_WRITE_FORBIDDEN);
  return applyMockCanvasItemVisibility(itemId, body);
}

/** Mock PATCH status — accept confirms the item; reject soft-deletes it. */
export async function mockUpdateCanvasItemStatus(
  _workspaceId: string,
  _storyId: string,
  itemId: string,
  status: CanvasItemStatusDecision,
): Promise<CanvasItemStubApi> {
  await wait(DATASET_LATENCY_MS);
  assertMockStoryEditor(ITEM_WRITE_FORBIDDEN);
  return applyMockCanvasItemStatus(itemId, status);
}

let mockContentItemCounter = 0;

/**
 * Mock POST canvas-items/content — echoes the submitted content/metadata
 * back as full widget data, same as the live backend's create response.
 */
export async function mockCreateCanvasContentItem(
  _workspaceId: string,
  _storyId: string,
  body: CanvasContentItemCreateRequestApi,
): Promise<CanvasContentItemApi> {
  await wait(DATASET_LATENCY_MS);
  assertMockStoryEditor(ITEM_WRITE_FORBIDDEN);
  mockContentItemCounter += 1;
  const item_id = `mock-${body.item_type}-${mockContentItemCounter}`;

  let created: CanvasContentItemApi;
  switch (body.item_type) {
    case "link":
      created = { item_id, type: "link", content: body.content, metadata: body.metadata ?? {}, status: "accepted" };
      break;
    case "embed":
      created = { item_id, type: "embed", content: body.content, metadata: body.metadata ?? {}, status: "accepted" };
      break;
    case "image":
      created = { item_id, type: "image", content: body.content, metadata: body.metadata ?? {}, status: "accepted" };
      break;
    case "text":
      created = { item_id, type: "text", content: body.content, metadata: body.metadata ?? {}, status: "accepted" };
      break;
    default: {
      const unknown = body as { item_type?: string };
      throw new Error(
        `mockCreateCanvasContentItem: unknown item_type "${unknown.item_type}"`,
      );
    }
  }
  // Persist so a later fetchCanvasWidget/update-content mock call finds it.
  widgetOverrides[item_id] = created;
  return created;
}

const _CONTENT_ITEM_TYPES = new Set(["link", "embed", "image", "text"]);

/** Merge a content-item content/metadata patch — content replaces wholesale,
 *  metadata merges key-by-key, mirroring the live PATCH .../content endpoint. */
export function applyMockCanvasContentItem(
  itemId: string,
  body: CanvasContentItemUpdateRequestApi,
): CanvasContentItemApi {
  const widget = currentWidget(itemId);
  if (!widget || !_CONTENT_ITEM_TYPES.has(widget.type)) {
    throw new Error(
      `applyMockCanvasContentItem: item "${itemId}" is not a link/embed/image/text item`,
    );
  }
  if (widget.type !== body.item_type) {
    throw new Error(
      `applyMockCanvasContentItem: item "${itemId}" is type "${widget.type}", not the declared "${body.item_type}"`,
    );
  }
  const contentWidget = widget as CanvasContentItemApi;
  const updated = {
    ...contentWidget,
    ...(body.content !== undefined ? { content: body.content } : {}),
    ...(body.metadata !== undefined
      ? { metadata: { ...contentWidget.metadata, ...body.metadata } }
      : {}),
  } as CanvasContentItemApi;
  widgetOverrides[itemId] = updated;
  return updated;
}

/** Mock PATCH .../canvas-items/:itemId/content — update a link/embed/image/text item. */
export async function mockUpdateCanvasContentItem(
  _workspaceId: string,
  _storyId: string,
  itemId: string,
  body: CanvasContentItemUpdateRequestApi,
): Promise<CanvasContentItemApi> {
  await wait(DATASET_LATENCY_MS);
  assertMockStoryEditor(ITEM_WRITE_FORBIDDEN);
  return applyMockCanvasContentItem(itemId, body);
}

const _REPOINTABLE_ITEM_TYPES = new Set([
  "report",
  "ingested_widget",
  "captured_widget",
]);

/** Re-point a report/ingested_widget/captured_widget item at a different
 *  search result, mirroring the live PATCH .../source endpoint's re-resolve
 *  behaviour (mockAddCanvasItem's external-report branch, applied in place). */
export function applyMockCanvasItemSource(
  itemId: string,
  body: CanvasItemSourceUpdateRequestApi,
): CanvasWidgetApi {
  const widget = currentWidget(itemId);
  if (!widget || !_REPOINTABLE_ITEM_TYPES.has(widget.type)) {
    throw new Error(
      `applyMockCanvasItemSource: item "${itemId}" is not a report/ingested_widget/captured_widget item`,
    );
  }
  if (widget.type !== body.item_type) {
    throw new Error(
      `applyMockCanvasItemSource: item "${itemId}" is type "${widget.type}", not the declared "${body.item_type}"`,
    );
  }
  const searchResult = MOCK_SEARCH_RESULTS.find(
    (result) =>
      result.result_id === body.source_id ||
      result.connector_asset_id === body.source_id,
  );
  const sourceName =
    body.source === "powerbi"
      ? "Power BI"
      : `${body.source.charAt(0).toUpperCase()}${body.source.slice(1)}`;
  const updated: CanvasWidgetApi = {
    ...widget,
    type: widget.type,
    source: {
      id: body.source,
      name: sourceName,
      logo_url: `/logos/${body.source}.svg`,
    },
    connector_asset_id: searchResult?.connector_asset_id ?? body.source_id,
    connection_id: searchResult?.connection_id ?? null,
    title: searchResult?.title ?? "Updated report",
    embed_url: searchResult?.embed_url ?? null,
    thumbnail_url: searchResult?.thumbnail_url ?? null,
  } as CanvasWidgetApi;
  widgetOverrides[itemId] = updated;
  return updated;
}

/** Mock PATCH .../canvas-items/:itemId/source — re-point a report item. */
export async function mockUpdateCanvasItemSource(
  _workspaceId: string,
  _storyId: string,
  itemId: string,
  body: CanvasItemSourceUpdateRequestApi,
): Promise<CanvasWidgetApi> {
  await wait(DATASET_LATENCY_MS);
  assertMockStoryEditor(ITEM_WRITE_FORBIDDEN);
  return applyMockCanvasItemSource(itemId, body);
}

// ─── Canvas items (v2) ────────────────────────────────────────────────────────
// Derived from the default canvas above rather than a fixture of its own: the
// ids then match what the v1 canvas renders, and a v2 delete drops the item
// off the v1 manifest too. On the server both read one story_canvas_items
// table. The Drug X memo story (MOCK_STORY_ID) has no v2 items: its list is
// empty and every item 404s, a delete included.

/**
 * v1 types the mock item GET and PATCH serve. The list returns every row, but
 * reports and content items have no full item here.
 */
const V2_ITEM_TYPES: Partial<Record<CanvasItemType, CanvasItemTypeApi>> = {
  chart: "chart",
  kpi: "kpi",
  narrative: "narrative",
  insight: "insight",
  summary: "summary",
};

interface MockEditableItem {
  query_model: QueryModelApi;
  /** Optional because a PATCH of `null` removes the key (RFC 7396). */
  binding?: BindingApi;
  format?: FormatApi;
}

/**
 * The one `chart_2` item, so both the editable and the legacy state are
 * reachable (`widget-chart-2` stays a legacy chart). Field ids come from the
 * data-model mock, so a catalog lookup resolves them.
 */
const EDITABLE_ITEM_SEEDS: Record<string, () => MockEditableItem> = {
  "widget-chart-1": () => ({
    query_model: {
      model_release_id: MOCK_MODEL_RELEASE_ID,
      analysis_view_id: null,
      baseline_filters: [],
    },
    binding: {
      chart_type: "bar",
      x: { field: { kind: "dimension_attribute", model_field_id: "region" } },
      y: [{ field: { kind: "measure", model_field_id: "sales" } }],
      color_by: null,
      trellis: null,
    },
    format: {
      title: { show: true, text: "Revenue performance vs target" },
      data_values: { show: false },
      axes: {
        x: { show: true, show_title: true, title: "Region", label_angle: 0 },
        y: { show: true, show_title: true, title: "Revenue ($)", label_angle: 0 },
      },
      color_palette: { kind: "built_in", value: "magicbi_palette" },
    },
  }),
};

/** Binding and format as PATCHed so far, seeded on first touch. */
const editableItems: Record<string, MockEditableItem> = {};

const findEditableItem = (itemId: string): MockEditableItem | undefined => {
  const seed = EDITABLE_ITEM_SEEDS[itemId];
  if (!editableItems[itemId] && seed) editableItems[itemId] = seed();
  return editableItems[itemId];
};

const BINDING_Y_MAX_ITEMS = 4;

/** What the backend says to a viewer who writes to an item. */
const ITEM_WRITE_FORBIDDEN = "Only the story owner or an editor can change this story.";

/** Every result is a deep copy, so a caller (a React Query cache, a test) cannot reach into the store. */
const cloneJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const itemNotFoundError = (itemId: string) =>
  mockNotFoundError(`Canvas item not found: ${itemId}`);

/**
 * The live item GET and PATCH find: on the default canvas and of a v2 type.
 * Anything else, a deleted item included, is the backend's 404. A rejected
 * item is already gone (applyMockCanvasItemStatus deletes it), matching the
 * guide's "filtered out server-side".
 */
const findV2Stub = (
  storyId: string,
  itemId: string,
): [CanvasItemStubApi, CanvasItemTypeApi] => {
  const stub =
    storyId === MOCK_STORY_ID
      ? undefined
      : currentMockStubs().find((item) => item.item_id === itemId);
  const itemType = stub && V2_ITEM_TYPES[stub.type];
  if (!stub || !itemType) throw itemNotFoundError(itemId);
  return [stub, itemType];
};

/**
 * DELETE takes any live row, reports and content items included, because the
 * soft delete is on story_canvas_items itself. A content item created this
 * session counts too: the mock keeps those as widgets, not manifest stubs.
 */
const assertDeletable = (storyId: string, itemId: string): void => {
  const isLive =
    storyId !== MOCK_STORY_ID &&
    !deletedItemIds.has(itemId) &&
    (currentMockStubs().some((item) => item.item_id === itemId) ||
      Boolean(widgetOverrides[itemId]));
  if (!isLive) throw itemNotFoundError(itemId);
};

const toItemSummary = (
  stub: CanvasItemStubApi,
  itemType: CanvasItemRowTypeApi,
): CanvasItemSummaryApi => {
  const editable = Boolean(EDITABLE_ITEM_SEEDS[stub.item_id]);
  return {
    id: stub.item_id,
    item_type: editable ? (itemType === "kpi" ? "kpi_2" : "chart_2") : itemType,
    pos_x: stub.position?.x ?? 0,
    pos_y: stub.position?.y ?? 0,
    pos_w: stub.position?.w ?? 0,
    pos_h: stub.position?.h ?? 0,
    ...(stub.position?.h_fixed != null
      ? { pos_h_fixed: stub.position.h_fixed }
      : {}),
    // v1 leaves insight rows' status null; v2 types it as a required string.
    status: stub.status ?? "accepted",
    editable,
  };
};

type SpecObject = Record<string, unknown>;

/** One axis's stored format onto its encoding channel, as Vega-Lite reads it. */
const compileAxis = (
  channel: SpecObject | undefined,
  axis: AxisFormatApi | undefined,
): SpecObject | undefined => {
  if (!channel || !axis) return channel;
  const title =
    axis.show_title === false ? null : axis.title ?? channel.title;
  return {
    ...channel,
    title,
    axis:
      axis.show === false
        ? null
        : {
            ...(channel.axis as SpecObject | undefined),
            ...(axis.label_angle === undefined ? {} : { labelAngle: axis.label_angle }),
          },
  };
};

/** The colours `format.color_palette` names: a built-in's, or a tenant palette's from its mock. */
const mockPaletteColors = (ref: PaletteRefApi | null | undefined): string[] => {
  if (!ref) return [];
  if (ref.kind === "built_in") {
    return chartColorPaletteDefinitions.find((palette) => palette.apiValue === ref.value)?.colors ?? [];
  }
  return findMockColorPalette(ref.value)?.colors ?? [];
};

/**
 * Stands in for the server's compile. It takes the v1 fixture spec with its
 * dataset rows inline (v2 specs carry `data.values`), then writes the stored
 * format onto it: axes, data values (`data_values.show`, through the Format tab's
 * own spec writer) and the palette's colours. `format.title` stays out of the
 * spec, because the widget header shows it. `binding` does not reshape the
 * chart: that is a real query the mock cannot run.
 */
const compileMockSpec = (
  itemId: string,
  format: FormatApi | undefined,
): VisualizationSpec | null => {
  const widget = currentWidget(itemId);
  if (widget?.type !== "chart") return null;
  const spec = widget.spec as SpecObject;
  const encoding = (spec.encoding as SpecObject | undefined) ?? {};
  const rows = (widget.data_id && MOCK_DATASETS[widget.data_id]?.rows) || [];
  const withAxes = {
    ...spec,
    data: { values: rows },
    encoding: {
      ...encoding,
      x: compileAxis(encoding.x as SpecObject | undefined, format?.axes?.x),
      y: compileAxis(encoding.y as SpecObject | undefined, format?.axes?.y),
    },
  } as VisualizationSpec;
  const showLabels = format?.data_values?.show;
  // The writer diffs against what the spec already draws, so only the labels move.
  const labelled =
    showLabels === undefined
      ? withAxes
      : applyChartFormatToSpec(
          withAxes,
          {
            ...chartFormatDefaults,
            ...readChartFormat(withAxes, widget.title),
            showDirectValues: showLabels,
          },
          chartFormatDefaults,
          widget.title,
        );
  return applyPaletteToSpec(labelled, mockPaletteColors(format?.color_palette));
};

const toFullItem = (
  stub: CanvasItemStubApi,
  itemType: CanvasItemTypeApi,
): CanvasItemFullApi => {
  const summary = toItemSummary(stub, itemType);
  const editable = findEditableItem(stub.item_id);
  if (!editable) {
    // Legacy: no query_model/binding/format keys at all, and the stored
    // chart spec unchanged.
    const widget = currentWidget(stub.item_id);
    return cloneJson(
      applyStoryFiltersToItem(
        { ...summary, spec: widget?.type === "chart" ? widget.spec : null },
        [],
      ),
    );
  }
  return cloneJson(
    applyStoryFiltersToItem(
      { ...summary, ...editable, spec: compileMockSpec(stub.item_id, editable.format) },
      listMockStoryFilterRows(),
    ),
  );
};

/**
 * Every editable item as stored, before any story filter narrows it. The
 * filter mock diffs these under the rows before and after a write to find the
 * items a write changed (`affected_item_ids`).
 */
export const listMockFilterableItems = (): CanvasItemFullApi[] =>
  currentMockStubs().flatMap((stub) => {
    const editable = findEditableItem(stub.item_id);
    const itemType = V2_ITEM_TYPES[stub.type];
    if (!editable || !itemType) return [];
    return [
      {
        ...toItemSummary(stub, itemType),
        ...editable,
        spec: compileMockSpec(stub.item_id, editable.format),
      } as CanvasItemFullApi,
    ];
  });

/** pydantic's 422 for a list over `max_length`, as api-service returns it. */
const tooManyYFieldsError = (count: number) =>
  mockValidationError("Request validation failed", [
    {
      loc: ["body", "binding", "y"],
      type: "too_long",
      msg: `List should have at most ${BINDING_Y_MAX_ITEMS} items after validation, not ${count}`,
    },
  ]);

export async function mockFetchCanvasItemSummaries(
  storyId: string,
  type?: CanvasItemListTypeFilterApi,
): Promise<CanvasItemListResponseApi> {
  await wait(MANIFEST_LATENCY_MS);
  if (storyId === MOCK_STORY_ID) return { items: [] };
  // Every live row, as api-service lists it: reports and content items keep
  // their v1 type, since `item_type` is a plain string there.
  const items = currentMockStubs().flatMap((stub) => {
    const summary = toItemSummary(stub, stub.type);
    return !type || summary.item_type === type ? [summary] : [];
  });
  return { items };
}

export async function mockFetchCanvasItem(
  storyId: string,
  itemId: string,
): Promise<CanvasItemFullApi> {
  await wait(DATASET_LATENCY_MS);
  return toFullItem(...findV2Stub(storyId, itemId));
}

let mockDuplicateCounter = 0;

/** The duplicate route: a clone of the item with a new id, beside the original (below when the row is full). */
export async function mockDuplicateCanvasItem(
  storyId: string,
  itemId: string,
  body: { title?: string; position?: ChartItemPositionApi } = {},
): Promise<CanvasItemFullApi> {
  const original = await mockFetchCanvasItem(storyId, itemId);
  mockDuplicateCounter += 1;
  const columns = original.pos_cols ?? 12;
  const fitsBeside = original.pos_x + original.pos_w * 2 <= columns;
  const copy: CanvasItemFullApi = {
    ...cloneJson(original),
    id: `mock-copy-${itemId}-${mockDuplicateCounter}`,
    pos_x: fitsBeside ? original.pos_x + original.pos_w : original.pos_x,
    pos_y: fitsBeside ? original.pos_y : original.pos_y + original.pos_h,
    ...(body.position ?? {}),
  };
  return body.title ? { ...copy, format: { ...copy.format, title: { ...copy.format?.title, text: body.title } } } : copy;
}
/**
 * Validates, then stores: a rejected PATCH leaves the item untouched, as on
 * the server. `mergePatch` is the same helper the optimistic cache write uses.
 */
export async function mockPatchCanvasItem(
  storyId: string,
  itemId: string,
  body: CanvasItemPatchRequestApi,
): Promise<CanvasItemFullApi> {
  await wait(DATASET_LATENCY_MS);
  assertMockStoryEditor(ITEM_WRITE_FORBIDDEN);
  const [stub, itemType] = findV2Stub(storyId, itemId);
  const current = findEditableItem(itemId);
  if (!current) {
    throw mockConflictError(`Canvas item is not editable: ${itemId}`);
  }
  const merged = mergePatch(current, { binding: body.binding, format: body.format });
  // Only the release can be re-pointed here; baseline filters change through `/filters`.
  const releaseId = body.query_model?.model_release_id;
  if (releaseId !== undefined && releaseId !== MOCK_MODEL_RELEASE_ID) {
    throw mockValidationError(`Model release not found: ${releaseId}`);
  }
  const next =
    releaseId === undefined
      ? merged
      : { ...merged, query_model: { ...merged.query_model, model_release_id: releaseId } };
  const yCount = next.binding?.y?.length ?? 0;
  if (yCount > BINDING_Y_MAX_ITEMS) throw tooManyYFieldsError(yCount);
  editableItems[itemId] = next;
  return toFullItem(stub, itemType);
}

/** Soft delete, shared with the v1 manifest: the canvas drops it on refetch. */
export async function mockDeleteCanvasItem(
  storyId: string,
  itemId: string,
): Promise<void> {
  await wait(DATASET_LATENCY_MS);
  assertMockStoryEditor(ITEM_WRITE_FORBIDDEN);
  assertDeletable(storyId, itemId);
  deletedItemIds.add(itemId);
}

// ─── Exported for tests ────────────────────────────────────────────────────────

export const __MOCK_MANIFEST_FOR_TESTS__ = MOCK_MANIFEST;
export const __MOCK_WIDGETS_FOR_TESTS__ = MOCK_WIDGETS;
export const __MOCK_DATASETS_FOR_TESTS__ = MOCK_DATASETS;
export const __MOCK_SEARCH_RESULTS_FOR_TESTS__ = MOCK_SEARCH_RESULTS;
export const __MOCK_STORY_ID__ = MOCK_STORY_ID;
export const __MOCK_WORKSPACE_ID__ = MOCK_WORKSPACE_ID;
